import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "@/lib/analytics";

/**
 * AnalyticsProvider
 *
 * Mount ONCE inside <BrowserRouter>. Responsibilities:
 *   1. On mount, call initAnalytics() — flips Google Consent Mode to granted
 *      and injects Microsoft Clarity if the user has already consented.
 *   2. Listen for `cookieconsent:change` events so we can (re-)initialize
 *      immediately when the user accepts cookies from the banner.
 *   3. Fire a GA4 page_view on every SPA route change (the inline gtag config
 *      has send_page_view=false so the initial visit is fired here too).
 *
 * The component renders nothing.
 */
export function AnalyticsProvider() {
  const location = useLocation();

  // Init once + re-init on consent change
  useEffect(() => {
    initAnalytics();
    const onConsentChange = () => initAnalytics();
    window.addEventListener("cookieconsent:change", onConsentChange);
    return () => window.removeEventListener("cookieconsent:change", onConsentChange);
  }, []);

  // SPA route tracking
  useEffect(() => {
    // Small delay so React Helmet / document.title updates before we record.
    const id = window.setTimeout(() => {
      trackPageView(location.pathname);
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  return null;
}
